import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { PixelArrow } from '@/components/generals/pixel-button'

import PixelEdge from '@/components/pixel/pixel-edge'

import { SERVICES } from '@/constants/content'
import { ICON_PLUS } from '@/constants/sprites'

const ServicesSection: FC = (): ReactNode => {
    const [openId, setOpenId] = useState<string | null>(SERVICES[0]?.id ?? null)

    return (
        <section
            id='services'
            aria-labelledby='services-title'
            className='relative mt-16 bg-bubblegum pattern-dots py-20 text-ink md:mt-24 md:py-28'
        >
            <PixelEdge color='var(--bubblegum)' seed={11} className='absolute inset-x-0 bottom-[calc(100%-1px)]' />

            <div className='mx-auto max-w-7xl px-5 md:px-8'>
                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <h2 id='services-title' className='font-heading text-[clamp(2.75rem,8vw,6.5rem)] leading-[0.88]'>
                        our services
                    </h2>

                    <p className='max-w-md text-base leading-relaxed font-semibold md:pb-2 md:text-lg'>
                        Six ways we help brands grow. Pick one, or let us combine them into a full campaign.
                    </p>
                </div>

                <ul className='mt-12 border-t-2 border-ink md:mt-16'>
                    {SERVICES.map((service, index) => {
                        const open = openId === service.id
                        const number = String(index + 1).padStart(2, '0')

                        return (
                            <li key={service.id} className='border-b-2 border-ink'>
                                <h3>
                                    <button
                                        type='button'
                                        id={`service-${service.id}`}
                                        aria-expanded={open}
                                        aria-controls={`service-panel-${service.id}`}
                                        onClick={() => setOpenId(open ? null : service.id)}
                                        className='group relative flex w-full cursor-pointer items-center gap-4 py-5 text-left md:gap-8 md:py-7'
                                    >
                                        <span
                                            aria-hidden='true'
                                            className='absolute inset-y-0 -inset-x-3 origin-left scale-x-0 bg-ink transition-transform duration-300 ease-[steps(6,end)] group-hover:scale-x-100 md:-inset-x-5'
                                        />

                                        <span className='relative w-7 shrink-0 font-sans text-sm font-bold tabular-nums group-hover:text-bubblegum md:w-10 md:text-base'>
                                            {number}
                                        </span>

                                        <span className='relative flex-1 font-heading text-[1.65rem] leading-[0.95] group-hover:text-paper sm:text-4xl md:text-5xl'>
                                            {service.title}
                                        </span>

                                        <span className='relative hidden max-w-xs text-sm leading-snug font-semibold group-hover:text-paper lg:block'>
                                            {service.summary}
                                        </span>

                                        <span className='relative grid size-10 shrink-0 place-items-center border-2 border-ink bg-white md:size-12'>
                                            <PixelSprite
                                                rows={ICON_PLUS}
                                                className={cn('size-3.5 transition-transform duration-200 ease-[steps(3,end)]', open && 'rotate-45')}
                                            />
                                        </span>
                                    </button>
                                </h3>

                                <div
                                    id={`service-panel-${service.id}`}
                                    role='region'
                                    aria-labelledby={`service-${service.id}`}
                                    inert={!open}
                                    className={cn(
                                        'grid transition-[grid-template-rows] duration-300 ease-[steps(6,end)]',
                                        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                >
                                    <div className='overflow-hidden'>
                                        <div className='flex flex-col gap-5 pb-7 md:pl-[4.5rem] lg:flex-row lg:items-center lg:justify-between'>
                                            <p className='max-w-xl text-base leading-relaxed font-semibold lg:hidden'>
                                                {service.summary}
                                            </p>

                                            <ul className='flex flex-wrap gap-2'>
                                                {service.deliverables.map(item => (
                                                    <li key={item} className='border-2 border-ink bg-white px-3 py-1.5 text-sm font-semibold'>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>

                                            <a
                                                href='#contacts'
                                                className='group/button inline-flex shrink-0 items-center gap-2 self-start font-heading text-lg underline decoration-2 underline-offset-[6px] hover:decoration-paper lg:self-auto'
                                            >
                                                start a project
                                                <PixelArrow />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>

            <PixelEdge color='var(--bubblegum)' seed={23} flip className='absolute inset-x-0 top-[calc(100%-1px)]' />
        </section>
    )
}

export default ServicesSection
